import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { STATUSPAGE_PATH } from '@atlassian/cssxp-typescommon';
import {
  FetchProductsForTransactionAccountQuery,
  FetchProductsForTransactionAccountQueryVariables,
  TransactionAccountProduct,
} from '@css-xp/graphtypes';
import { getEntitlementRouteCCP } from '@atlassian/cssxp-utils-entitlement';
import EntitlementIcon from '../../../entitlement-icon/entitlement-icon';
import FormattedDate from '../../../formatted/src/date';
import { ColGroup, SearchResultsTable, TableBody, TableHead } from '../../../search-results-table';
import SimpleTD from '../../../simple-td/src/simple-td';
import { fetchProductsForTransactionAccount } from './queries/fetch-products-for-transaction-account';
import { useCallback } from 'react';
import { PAGE_TAB_SEARCH_RESULTS_LOAD_MORE } from './products-tab';
import Tooltip from '@atlaskit/tooltip';

interface ProductTabProps {
  id?: string;
  label?: string;
}

const columns = [
  {
    name: 'Product',
    customClassNames: 'col-20',
  },
  {
    name: 'ID',
    customClassNames: 'col-20',
  },
  {
    name: 'Billing frequency',
    customClassNames: 'col-20',
  },
  {
    name: 'Next bill',
    customClassNames: 'col-20',
  },
  {
    name: 'Last billed',
    customClassNames: 'col-20',
  },
  {
    name: '',
  },
];

const link = (product: TransactionAccountProduct) => {
  return getEntitlementRouteCCP(product.productId || '', product.entitlementNumber || '');
};

const CloudProduct = ({ product }: { product: TransactionAccountProduct }): JSX.Element => {
  return (
    <Link to={link(product)} onClick={(e) => e.stopPropagation()} className="avatar-container">
      {' '}
      {!!product?.productId && (
        <EntitlementIcon productId={product?.productId} isCloud={true} className="avatar medium" />
      )}
      {product?.productName ?? ''}
    </Link>
  );
};

const ProductTabTxA = ({ id, label }: ProductTabProps) => {
  const { data, loading, error, fetchMore } = useQuery<
    FetchProductsForTransactionAccountQuery,
    FetchProductsForTransactionAccountQueryVariables
  >(fetchProductsForTransactionAccount, {
    variables: {
      id: id ?? '',
    },
    skip: !id,
  });

  const totalCount = data?.transactionAccount?.products?.totalCount ?? 0;

  const hasNoResults = !loading && (!data || data?.transactionAccount?.products?.totalCount === 0);
  const products = data?.transactionAccount?.products?.nodes;
  const resultsCount = products?.length ?? 0;
  const cursor = data?.transactionAccount?.products?.pageInfo?.endCursor;

  const handleOnLoadMoreClick = useCallback(() => {
    return fetchMore({
      variables: {
        searchOptions: {
          cursor: cursor,
          first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
        },
      },
      //@ts-ignore
      updateQuery: (
        previousResult,
        { fetchMoreResult }: { fetchMoreResult: FetchProductsForTransactionAccountQuery }
      ) => {
        if (
          fetchMoreResult?.transactionAccount?.products?.nodes &&
          previousResult?.transactionAccount?.products?.nodes
        ) {
          fetchMoreResult.transactionAccount.products.nodes = [
            ...previousResult.transactionAccount.products.nodes,
            ...fetchMoreResult.transactionAccount.products?.nodes,
          ];
        }
        return fetchMoreResult;
      },
    });
  }, [fetchMore]);

  const optionalTooltip = (billingStatus: string | undefined | null) => {
    const optionalTooltips = [
      {
        predicate: billingStatus === 'SUSPENDED',
        description: 'Deactivated',
      },
      {
        predicate: billingStatus === 'INACTIVE',
        description: 'Deleted',
      },
    ];

    return optionalTooltips.find((tooltip) => tooltip.predicate === true);
  };

  return (
    <SearchResultsTable
      id="transaction-account-products"
      isLoading={loading}
      error={error?.toString()}
      noResults={hasNoResults}
      heading={label}
      loadMore={{
        showLoadMore: totalCount !== 0 && resultsCount < totalCount,
        totalCount,
        resultsCount,
        onLoadMoreClick: handleOnLoadMoreClick,
      }}
      entityName="transaction account product"
    >
      <ColGroup columns={columns} />
      <TableHead columns={columns} />
      <TableBody>
        {(products ?? []).map(
          //@ts-ignore
          (product: TransactionAccountProduct, index: number) => {
            const canShowToolTip = optionalTooltip(product?.billingStatus);
            return (
              <tr
                className={classNames('search-result', {
                  deactivated: product?.billingStatus === 'SUSPENDED',
                  deleted: product?.billingStatus === 'INACTIVE',
                })}
                key={`search-result-${index}`}
              >
                <td>
                  {product?.productName ? (
                    canShowToolTip !== undefined ? (
                      <Tooltip position="bottom" content={canShowToolTip.description}>
                        <CloudProduct product={product} />
                      </Tooltip>
                    ) : (
                      <CloudProduct product={product} />
                    )
                  ) : (
                    'Not available'
                  )}
                </td>
                {product?.entitlementNumber ? <SimpleTD text={product?.entitlementNumber} /> : 'n/a'}
                <td>{product?.billingFrequency ? capitalize(product?.billingFrequency) : 'n/a'}</td>
                <td>{product?.latestPeriodEndDate ? <FormattedDate date={product?.latestPeriodEndDate} /> : 'n/a'}</td>
                <td>
                  {product?.latestPeriodStartDate ? <FormattedDate date={product?.latestPeriodStartDate} /> : 'n/a'}
                </td>
                <td />
              </tr>
            );
          }
        )}
      </TableBody>
    </SearchResultsTable>
  );
};

export default ProductTabTxA;
